/* Copyright (c) 2020 Paul Nebel */

import { Machine, assign, interpret } from 'xstate';
import * as Tone from 'tone';
import { forEach } from 'lodash';

import PlayerMachine from './player-machine';

const countdown = assign({
    timer: (context) =>
        context.timer >= 0 ? context.timer - 1 : context.timer,
});
const beatTime = assign({
    beat: (context) => context.beat + 1,
});
const resetTimer = assign({
    // the timer value needs to be set to the number of quarter notes in the repeat loop - this can vary
    // consider passing an event with an associated value?
    timer: (context) => context.quarterNotesInMaxPlayerLoop,
    beat: () => -1,
});
const introComplete = (context) => {
    return context.timer < 0;
};
const setLoopDuration = assign({
    quarterNotesInMaxPlayerLoop: (context, event) =>
        event.quarterNotesInMaxPlayerLoop,
    timer: (context, event) => event.quarterNotesInMaxPlayerLoop,
});

// private variables
let machineService,
    machineState,
    playerMachine,
    mht,
    mlt,
    loopId,
    playerLoopDurationInSixteenths;
let finalIndex = false;

const timeToTotalSixteenths = (duration) => {
    const bbs = Tone.Time(duration)
        .toBarsBeatsSixteenths()
        .split(':')
        .reverse();
    let totalSixteenths = 0;
    forEach(bbs, (n, idx) => {
        totalSixteenths = totalSixteenths + n * Math.pow(4, idx);
    });
    return totalSixteenths;
};

class MetronomeMachine {
    constructor() {
        const machine = Machine(
            {
                id: 'metronome',
                initial: 'inactive',
                context: {
                    // the timer value needs to be set to the number of quarter notes in the repeat loop - this can vary
                    quarterNotesInMaxPlayerLoop: 4,
                    timer: 4,
                    beat: -1,
                },
                states: {
                    inactive: {
                        on: {
                            START: 'intro',
                            SET_LOOP_DURATION: { actions: 'setLoopDuration' },
                        },
                    },
                    intro: {
                        always: {
                            target: 'active',
                            actions: 'beatTime',
                            cond: 'introComplete',
                        },
                        on: {
                            STOP: {
                                target: 'inactive',
                                actions: 'resetTimer',
                            },
                            PAUSE: 'pausedIntro',
                            COUNTDOWN: {
                                target: 'intro',
                                actions: 'countdown',
                            },
                        },
                    },
                    active: {
                        on: {
                            STOP: {
                                target: 'inactive',
                                actions: 'resetTimer',
                            },
                            PAUSE: 'pausedActive',
                            BEAT_TIME: { actions: 'beatTime' },
                        },
                    },
                    pausedIntro: {
                        on: {
                            START: 'intro',
                            STOP: {
                                target: 'inactive',
                                actions: 'resetTimer',
                            },
                        },
                    },
                    pausedActive: {
                        on: {
                            START: 'active',
                            STOP: {
                                target: 'inactive',
                                actions: 'resetTimer',
                            },
                        },
                    },
                },
            },
            {
                actions: {
                    countdown,
                    beatTime,
                    resetTimer,
                    setLoopDuration,
                },
                guards: {
                    introComplete,
                },
            }
        );
        machineService = interpret(machine).onTransition(
            (state) => (machineState = state)
        );
        playerMachine = PlayerMachine;

        // sound
        const volume = -18;
        const loopDuration = '4n';

        // const freeverb = new Tone.Reverb().toDestination();
        // freeverb.dampening = 1000;
        mht = new Tone.NoiseSynth({
            volume,
            noise: { type: 'white' },
        }).toDestination();
        // mht.connect(freeverb);
        mlt = new Tone.NoiseSynth({
            volume,
            noise: { type: 'pink' },
        }).toDestination();

        const metroSoundCallback = (time) => {
            const quarterNote = Tone.Transport.position.split(':')[1];
            const currentState = machineState.value;
            // only skip responding after the first beat
            if (
                !(
                    finalIndex &&
                    quarterNote === '0' &&
                    machineState.context.beat > 0 &&
                    currentState === 'active'
                )
            ) {
                if (quarterNote === '0') {
                    mht.triggerAttackRelease('8n', time + 0.025);
                } else {
                    mlt.triggerAttackRelease('8n', time + 0.025);
                }
                Tone.Draw.schedule(() => {
                    switch (currentState) {
                        case 'inactive':
                            machineService.send('START');
                            break;
                        case 'intro':
                            machineService.send('COUNTDOWN');
                            break;
                        case 'active':
                            machineService.send({
                                type: 'BEAT_TIME',
                                quarterNote,
                            });
                            break;
                        default:
                        // do nothing
                    }
                }, time);
            }
        };
        const { id, loopDuration: playerLoopDuration } =
            playerMachine.addCallback(metroSoundCallback, loopDuration);
        loopId = id;
        this.loopDuration = playerLoopDuration.value;
    }

    start() {
        machineService.start();
        return this;
    }

    stop() {
        machineService.stop();
        return this;
    }

    onTransition(callback) {
        machineService.onTransition(callback);
        return this;
    }

    resetTimer() {
        machineService.send('STOP');
    }

    pauseTimer() {
        machineService.send('PAUSE');
    }

    startTimer() {
        machineService.send('START');
    }

    set finalIndex(value) {
        if (finalIndex !== value) {
            finalIndex = value;
        }
    }

    get volume() {
        return mht.volume;
    }

    set volume(value) {
        if (mht.volume !== value) {
            mht.volume.value = value;
            mlt.volume.value = value;
        }
    }

    get bpm() {
        return playerMachine.bpm;
    }

    set bpm(value) {
        if (playerMachine.bpm !== value) {
            playerMachine.bpm.value = value;
        }
    }

    set loopDuration(value) {
        const loopDurationInSixteenths = timeToTotalSixteenths(value);
        if (playerLoopDurationInSixteenths !== loopDurationInSixteenths) {
            playerLoopDurationInSixteenths = loopDurationInSixteenths;
            machineService.send({
                type: 'SET_LOOP_DURATION',
                quarterNotesInMaxPlayerLoop: Math.floor(
                    loopDurationInSixteenths / 4
                ),
            });
        }
    }
}

export default MetronomeMachine;
